import { ObservationSummaryContainerView } from '../../coaching-detail';
import { FocusShallow } from '../../coaching-tags/tags/tags.model';
import { createReducer } from '../../store.model';

import {
    ActionType,
    GoToObservationAction,
    NextObservationAction,
    PrevObservationAction,
    ReloadAction,
    SetInReviewAction,
} from './observation-review.action';
import {
    defaultState,
    ObservationReviewState,
} from './observation-review.state';

const { builder, reducer } = createReducer<ActionType, ObservationReviewState>(
    defaultState(),
);

builder.add<ReloadAction>(
    ActionType.COACHING_OBSERVATION_REVIEW__RELOAD,
    (state, action) => {
        const nextObservationsAll = action.payload.observations || [];
        let nextIndex = 0;
        let nextObservations: ObservationSummaryContainerView[];

        if (action.payload.focus) {
            nextObservations = nextObservationsAll.filter((e) =>
                isMatchingFocusContainer(action.payload.focus, e),
            );
        }

        if (action.payload.observationId != null) {
            nextIndex = nextObservations.findIndex(
                (e) => e.observationId === action.payload.observationId,
            );
        }

        const ret = navigate('current', nextIndex, nextObservations);
        nextIndex = ret.nextIndex;

        return {
            ...state,
            observations: nextObservations,
            observationIndex: nextIndex,
            selectedObservation: ret.nextObservation,
            inReview: action.payload.inReview,
            focus: action.payload.focus,
        };
    },
);

builder.add<GoToObservationAction>(
    ActionType.COACHING_OBSERVATION_REVIEW__GO_TO_OBSERVATION,
    (state, action) => {
        const index = state.observations.findIndex(
            (e) => e.observationId === action.payload.observationId,
        );
        let nextIndex = state.observationIndex;
        let nextObservation = state.selectedObservation;
        if (index !== -1) {
            nextIndex = index;
            nextObservation = state.observations[nextIndex];
        }

        return {
            ...state,
            observationIndex: nextIndex,
            selectedObservation: nextObservation,
        };
    },
);

builder.add<SetInReviewAction>(
    ActionType.COACHING_OBSERVATION_REVIEW__SET_IN_REVIEW,
    (state, action) => {
        return {
            ...state,
            inReview:
                action.payload.inReview == null
                    ? !state.inReview
                    : action.payload.inReview,
        };
    },
);

builder.add<PrevObservationAction>(
    ActionType.COACHING_OBSERVATION_REVIEW__PREV_OBSERVATION,
    (state) => {
        const ret = navigate(
            'prev',
            state.observationIndex,
            state.observations,
        );

        return {
            ...state,
            observationIndex: ret.nextIndex,
            selectedObservation: ret.nextObservation,
            inReview: true,
        };
    },
);

builder.add<NextObservationAction>(
    ActionType.COACHING_OBSERVATION_REVIEW__NEXT_OBSERVATION,
    (state) => {
        const ret = navigate(
            'next',
            state.observationIndex,
            state.observations,
        );

        return {
            ...state,
            observationIndex: ret.nextIndex,
            selectedObservation: ret.nextObservation,
            inReview: true,
        };
    },
);

interface Pos {
    nextIndex: number;
    nextObservation: ObservationSummaryContainerView;
}

const isMatchingFocusContainer = (
    focus: FocusShallow,
    observation: ObservationSummaryContainerView,
): boolean => {
    return (
        focus &&
        observation.behaviourId != null &&
        observation.behaviourId === focus.behaviourId &&
        observation.subBehaviourId === focus.subBehaviourId
    );
};

const navigate = (
    direction: 'prev' | 'current' | 'next',
    index: number,
    observations: ObservationSummaryContainerView[],
): Pos => {
    let nextIndex: number;
    if (direction === 'prev') {
        nextIndex = index - 1;
        if (nextIndex < 0 || nextIndex >= observations.length) {
            nextIndex = observations.length - 1;
        }
    }
    if (direction === 'current') {
        nextIndex = index;
        if (nextIndex < 0) {
            nextIndex = 0;
        }
        if (nextIndex >= observations.length) {
            nextIndex = observations.length - 1;
        }
    }
    if (direction === 'next') {
        nextIndex = index + 1;
        if (nextIndex < 0 || nextIndex >= observations.length) {
            nextIndex = 0;
        }
    }
    const nextObservation = observations[nextIndex];

    return { nextIndex, nextObservation };
};

export default {
    observationReview: {
        root: reducer,
    },
};
